// Ephemeral channel client — second WS + encrypt/decrypt fanout.

import { decodeEphemeralFrame, encodeEphemeralFrame } from './codec';
import { base64UrlToConversationId, conversationIdToBase64Url } from './ids';
import { EphemeralKeyring, type EphemeralSecretExporter } from './keyring';
import {
  createEphemeralSocket,
  type EphemeralConnState,
  type EphemeralSocket,
} from './socket.web';
import type {
  EphemeralErrorFrame,
  EphemeralEvent,
  EphemeralPayload,
  EphemeralType,
  PresenceState,
} from './types';
import { log } from '../log/log';

export interface EphemeralClientConfig {
  readonly wsUrl: string;
  readonly getToken: () => string | null;
  readonly getDeviceId: () => Promise<Uint8Array>;
  readonly exporter: EphemeralSecretExporter;
  /** MLS epoch per conversation — used for key rotation + nonce. */
  readonly getEpoch?: (conversationId: Uint8Array) => Promise<number>;
  readonly onReconnect?: () => Promise<void>;
}

export class EphemeralClient {
  private readonly config: EphemeralClientConfig;
  private readonly keyring: EphemeralKeyring;
  private socket: EphemeralSocket | null = null;
  private socketSub: { unsubscribe(): void } | null = null;
  private readonly subscribed = new Set<string>();
  private readonly eventHandlers = new Set<(event: EphemeralEvent) => void>();
  private readonly stateHandlers = new Set<(state: EphemeralConnState) => void>();
  private ownDeviceId: Uint8Array | null = null;
  private started = false;

  constructor(config: EphemeralClientConfig) {
    this.config = config;
    this.keyring = new EphemeralKeyring(config.exporter);
  }

  /** Open the WebSocket and begin receiving frames. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.socket = createEphemeralSocket({
      url: this.config.wsUrl,
      getToken: this.config.getToken,
      ...(this.config.onReconnect != null ? { onReconnect: this.config.onReconnect } : {}),
    });
    this.socketSub = this.socket.subscribe(
      (buf) => {
        void this.handleBinary(buf);
      },
      (text) => {
        this.handleText(text);
      },
      (state) => {
        for (const h of this.stateHandlers) {
          h(state);
        }
        if (state === 'connected') {
          this.resubscribeAll();
        }
      },
    );
  }

  stop(): void {
    this.started = false;
    this.socketSub?.unsubscribe();
    this.socketSub = null;
    this.socket = null;
    this.subscribed.clear();
    this.keyring.clear();
    this.ownDeviceId = null;
  }

  onEvent(handler: (event: EphemeralEvent) => void): () => void {
    this.eventHandlers.add(handler);
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  onState(handler: (state: EphemeralConnState) => void): () => void {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  /** Subscribe to fanout for the given conversations (union). */
  subscribe(conversationIds: ReadonlyArray<Uint8Array>): void {
    for (const id of conversationIds) {
      this.subscribed.add(conversationIdToBase64Url(id));
    }
    this.sendControl({
      kind: 'subscribe',
      conversation_ids: [...this.subscribed],
    });
  }

  unsubscribe(conversationIds: ReadonlyArray<Uint8Array>): void {
    const removed: string[] = [];
    for (const id of conversationIds) {
      const b64 = conversationIdToBase64Url(id);
      if (this.subscribed.delete(b64)) {
        removed.push(b64);
      }
    }
    if (removed.length > 0) {
      this.sendControl({
        kind: 'unsubscribe',
        conversation_ids: removed,
      });
    }
  }

  /** Conversation ids currently subscribed for fanout. */
  getSubscribedConversationIds(): Uint8Array[] {
    const out: Uint8Array[] = [];
    for (const b64 of this.subscribed) {
      try {
        out.push(base64UrlToConversationId(b64));
      } catch {
        // skip malformed entries
      }
    }
    return out;
  }

  async publishPresence(
    conversationId: Uint8Array,
    state: PresenceState,
  ): Promise<void> {
    await this.publish(conversationId, 'presence', { state });
  }

  async publishPresenceToSubscribed(state: PresenceState): Promise<void> {
    const ids = this.getSubscribedConversationIds();
    await Promise.all(ids.map((id) => this.publishPresence(id, state)));
  }

  async publish(
    conversationId: Uint8Array,
    type: EphemeralType,
    payload: EphemeralPayload = {},
  ): Promise<void> {
    if (this.socket == null) {
      log.debug('ephemeral.publish.skipped', { reason: 'not_started' });
      return;
    }
    const deviceId = await this.ensureDeviceId();
    if (this.config.getEpoch != null) {
      const epoch = await this.config.getEpoch(conversationId);
      this.keyring.setEpoch(conversationId, epoch);
    }
    const frame = await encodeEphemeralFrame({
      conversationId,
      senderDevice: deviceId,
      type,
      payload,
      keyring: this.keyring,
    });
    this.socket.send(frame);
    log.debug('ephemeral.publish.sent', { type });
  }

  private async ensureDeviceId(): Promise<Uint8Array> {
    if (this.ownDeviceId !== null) {
      return this.ownDeviceId;
    }
    const id = await this.config.getDeviceId();
    if (id.length !== 32) {
      throw new Error('ephemeral: device id must be 32 bytes');
    }
    this.ownDeviceId = id;
    return id;
  }

  private sendControl(frame: Record<string, unknown>): void {
    if (this.socket == null) return;
    this.socket.send(JSON.stringify(frame));
  }

  private resubscribeAll(): void {
    if (this.subscribed.size === 0) return;
    this.sendControl({
      kind: 'subscribe',
      conversation_ids: [...this.subscribed],
    });
  }

  private handleText(text: string): void {
    try {
      const parsed = JSON.parse(text) as EphemeralErrorFrame;
      if (parsed?.kind === 'error') {
        log.warn('ephemeral.control.error', { code: parsed.code });
      }
    } catch {
      log.debug('ephemeral.control.unparsed');
    }
  }

  private async handleBinary(buf: ArrayBuffer): Promise<void> {
    const wire = new Uint8Array(buf);
    try {
      const decoded = await decodeEphemeralFrame(wire, this.keyring);
      const own = await this.ensureDeviceId();
      if (bytesEqual(decoded.senderDevice, own)) {
        return;
      }
      const event: EphemeralEvent = {
        conversationId: decoded.conversationId,
        senderDevice: decoded.senderDevice,
        epoch: decoded.epoch,
        type: decoded.type,
        payload: decoded.payload,
      };
      for (const h of this.eventHandlers) {
        h(event);
      }
    } catch (err) {
      log.debug('ephemeral.frame.drop', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
